/**
 * Created by wwei on 2017-06-29.
 */
import React, { Component } from "react";
import { Route, Switch, Redirect } from "react-router-dom";
import EventListener from "react-event-listener";
import ReactGA from "react-ga";
import { connect } from "react-redux";
import queryString from "query-string";

import { AsyncMainPage, AsyncInfastructureMain } from "./CodeSplitting";
import A4BMainPage from "./tenantportal/a4b/main/A4BMainPage";
import LandingPage from "./LandingPage";
import writeLog from "./common/functions/LogUtil.js";
import Settings from "./common/Settings";
import Constants, { URL, ROLES } from "./common/Constants";
import getUrl, { signOut } from "./common/functions/CommonUtils";
import GA_DEFINITION from "./common/functions/GAUtils";
import { httpRequest as request } from "./common/functions/CustomRequest";
import Waiting from "./common/components/Waiting";

import {
  STORE_FEATURE_CONFIGS,
  STORE_ENTITLEMENTS,
  STORE_SYSTEM_CONFIGS
} from "./modules/config";

import i18n from "./i18n";

class Dispatch extends Component {
  timerId = 0;

  constructor() {
    super();
    this.state = {
      loaded: false
    };
  }

  componentWillMount() {
    const path = this.props.location.search;
    if (path && path.indexOf("logout=1") !== -1) {
      signOut();
    }
  }

  componentDidMount() {
    if (!Settings.LOCALIZATION) {
      i18n.changeLanguage("en-us");
    }

    this.getSystemConfigs()
      .then(this.getBaseSessionInfo)
      .then(this.getFeatureConfig)
      .then(this.getCompanyDetails)
      .then(this.getUserPrivileges)
      .then(this.getLicences)
      .then(this.startTimer)
      .then(this.doNavigate)
      .then(flag => this.setState({ loaded: flag }))
      .catch(this.handleSetupErrors);
  }

  getSystemConfigs = () =>
    request("/api/getSystemConfigs", "get", null).then(res => {
      this.props.storeSystemConfigs(res.body);
      if (res.body.gaTrackingId) {
        ReactGA.initialize(res.body.gaTrackingId);
      }
      return res.body;
    });

  /**
   * Fetches initial session info - services, API tokens, tenant and user IDs.  Stores them in sessionStorage.
   */
  getBaseSessionInfo = () =>
    request("/api/getUserInfo", "get", null).then(response => {
      const res = response.body.data;
      const services = res.services.reduce((servicesObj, ser) => {
        servicesObj[ser.name] = ser.versions.reduce((serviceObj, version) => {
          serviceObj[version.version] = version.url;
          return serviceObj;
        }, {});
        return servicesObj;
      }, {});
      sessionStorage.setItem("services", JSON.stringify(services));
      sessionStorage.setItem("tenantId", res.tid);
      sessionStorage.setItem("uaid", res.uaid);
      return res;
    });

  /**
   * Fetches the configured features from the server-side API and puts them in the Redux Store.
   */
  getFeatureConfig = () =>
    request(
      "/api/" + sessionStorage.getItem("tenantId") + "/features",
      "get",
      {}
    ).then(response => {
      this.props.storeFeatureConfigs(response.body);
      return response;
    });

  /**
   * Fetches the company name from the API, stores it in sessionStorage.
   */
  getCompanyDetails = () =>
    request(getUrl(Constants.tenantManager, true, true, "/self"), "get", {})
      .then(res => {
        const companyName = res.body.companyName;
        sessionStorage.setItem("companyName", companyName);
        return res.body;
      })
      .catch(err => {
        throw err;
      });

  /**
   * Fetches user information from API.  Username, uuid, names, ID, and roles.  Stores it in sessionStorage.
   */
  getUserPrivileges = () =>
    request(
      getUrl(Constants.userInfo, true, true, "/myprofile"),
      "get",
      {}
    ).then(response => {
      const profile = response.body;
      sessionStorage.setItem("username", profile.email);
      sessionStorage.setItem("uuid", profile.uuid);
      sessionStorage.setItem("lname", profile.localProf.lname);
      sessionStorage.setItem("fname", profile.localProf.fname);
      sessionStorage.setItem("userId", profile.localProf.id);
      sessionStorage.setItem("homeTid", profile.homeTenant.tid);
      sessionStorage.setItem("roles", JSON.stringify(profile.roles));
      sessionStorage.setItem(
        Constants.USER_PERMISSIONS,
        JSON.stringify(profile.permissions)
      );
      sessionStorage.setItem("isLocalUser", profile.isLocalUser);
      return profile.permissions;
    });

  /**
   * Fetches the list of licenses for the current tenant.  Stores the list of unique active featureCodes in Redux.
   */
  getLicences = () =>
    request("/api/features/postLogin", "get", null).then(response => {
      let features = [];
      if (response.body.licensedFeatures) {
        features = response.body.licensedFeatures.map(e => e.toUpperCase());
      }
      this.props.storeEntitlements(features);
      return response;
    });

  /**
   * With all prep done, redirects to the appropriate page based on the initial URL.
   */
  doNavigate = () => {
    let targetUrl = undefined;
    /**
     * Record login log.
     */
    const params = queryString.parse(this.props.location.search);
    if (params && Object.keys(params).indexOf("loginSucceed") !== -1) {
      writeLog("login", "login", "Login.");
      targetUrl = URL.ROOT;
    }

    let tmp = sessionStorage.getItem("dispatch_target_path");
    if (tmp) {
      sessionStorage.removeItem("dispatch_target_path");
      if (!tmp.startsWith("/api/")) {
        targetUrl = tmp;
      }
    }

    const roles = JSON.parse(sessionStorage.getItem("roles"));
    if (roles.length === 1 && roles[0] === "Default") {
      console.log("No role.");
      targetUrl = URL.NO_ROLE;
    } else if (
      roles.length === 2 &&
      roles.indexOf(ROLES.PDMSSP_USER.value) !== -1
    ) {
      const redirectUrl = this.props.systemConfigs.pdmssp + "/obinet/?sso=true";
      console.log("PDMS-SP User, redirecting to " + redirectUrl);
      window.location.href = redirectUrl;
      return false;
    }

    if (targetUrl) {
      this.props.history.replace(targetUrl);
    }
    return true;
  };

  /**
   * If any errors occur while fetching features, immediately kick to login screen.  I disagree with this.
   */
  handleSetupErrors = err => {
    if (this.props.location.pathname !== URL.ROOT) {
      sessionStorage.setItem(
        "dispatch_target_path",
        this.props.location.pathname
      );
      console.log("Recording URL: " + this.props.location.pathname);
    }

    if (err.status === 401) {
      console.error("401 error", err);
      window.location.href = this.props.systemConfigs.loginUrl;
    } else {
      console.error("Non-401 Error thrown!", err);
      this.props.history.replace(URL.NO_ROLE);
    }
  };

  /**
   * Starts a 1hr session timer.
   */
  startTimer = () => {
    if (this.timerId === 0) {
      const configTimeout = this.props.systemConfigs.sessionTimeout;
      const timeout =
        configTimeout !== undefined && configTimeout > 0
          ? configTimeout
          : 59 * 60 * 1000;
      this.timerId = setTimeout(this.logout, timeout);
      //console.log("Timer start.");
    }
    return this.timerId;
  };

  /**
   * Resets the session timer.  Triggered on clicks.
   */
  resetTimer = () => {
    clearTimeout(this.timerId);
    this.timerId = 0;
    this.startTimer();
  };

  /**
   * Ends the session and then redirects to login screen.
   */
  logout = () => {
    signOut();
  };

  /**
   * Report the click to Google Analytics, and reset the session timer.
   */
  handleOnClick = e => {
    let id = this.getTargetId(e.target, 0);
    if (id !== null) {
      let obj = GA_DEFINITION[id];
      if (obj !== undefined) {
        if (obj instanceof Array) {
          const path = e.view.location.pathname;
          let tmp = obj.filter(o => o.pathname === path);
          if (tmp.length > 0) {
            obj = tmp[0];
            ReactGA.event(obj);
          }
        } else {
          ReactGA.event(obj);
        }
        this.resetTimer();
      }
    }
  };

  /**
   * Traverse to find ID of the element clicked.
   */
  getTargetId = (node, loop) => {
    if (node) {
      if (node.id) {
        return node.id;
      } else if (loop > 2) {
        return null;
      } else {
        return this.getTargetId(node.parentNode, loop++);
      }
    } else {
      return null;
    }
  };

  render = () => {
    return (
      <div>
        <EventListener target={document} onClick={this.handleOnClick} />
        {this.state.loaded ? (
          <Switch>
            <Route exact path={URL.ROOT} component={LandingPage} />
            <Route
              path={URL.TENANT_PORTAL.CLOUD_DEVICES}
              component={AsyncInfastructureMain}
            />
            <Route path={URL.TENANT_PORTAL.ROOT} component={AsyncMainPage} />
            <Route path={URL.A4B.ROOT} component={A4BMainPage} />
            <Redirect to={URL.ROOT} />
          </Switch>
        ) : (
          <Waiting />
        )}
      </div>
    );
  };
}
const mapStateToProps = state => ({
  featureConfigs: state.config.featureConfigs,
  systemConfigs: state.config.systemConfigs
});

const mapDispatchToProps = dispatch => ({
  storeSystemConfigs: systemConfigs => {
    dispatch({
      type: STORE_SYSTEM_CONFIGS,
      systemConfigs
    });
  },
  storeFeatureConfigs: featureConfigs => {
    dispatch({
      type: STORE_FEATURE_CONFIGS,
      featureConfigs
    });
  },
  storeEntitlements: entitlements => {
    dispatch({
      type: STORE_ENTITLEMENTS,
      entitlements
    });
  }
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Dispatch);



// WEBPACK FOOTER //
// ./src/Dispatch.js